/**
 * Mandatory Confidentiality Undertaking.
 *
 * Two ways out of this screen and no third: Agree, or Sign out. There is no
 * dismiss, no Escape, no navigation shell, and nothing here decides access -
 * the server refuses every other endpoint with 403 NDA_REQUIRED until the
 * undertaking is accepted, so closing the tab or editing the URL changes
 * nothing.
 *
 * The language is chosen from the employee's nationality by the backend and is
 * not selectable, by the owner's instruction.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, Check, FileText, Loader2, LogOut, ShieldCheck } from 'lucide-react';

import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COPY = {
  en: {
    banner: 'Required before you continue',
    heading: 'Confidentiality Undertaking',
    intro: 'Please read this undertaking. You cannot use the system until you accept it.',
    scrollHint: 'Scroll to the end to continue',
    confirm: 'I have read and I accept this undertaking.',
    agree: 'Agree and continue',
    signOut: 'Sign out',
    saving: 'Recording your acceptance…',
    shownIn: 'Shown in',
    failed: 'Could not record your acceptance. Please try again.',
    pdfNote: 'A signed copy will be saved to your documents.',
  },
  ar: {
    banner: 'مطلوب قبل المتابعة',
    heading: 'تعهد بالسرية',
    intro: 'يرجى قراءة هذا التعهد. لا يمكنك استخدام النظام قبل الموافقة عليه.',
    scrollHint: 'انزل إلى نهاية النص للمتابعة',
    confirm: 'أقررت بأنني قرأت هذا التعهد وأوافق عليه.',
    agree: 'أوافق ومتابعة',
    signOut: 'تسجيل الخروج',
    saving: 'جارٍ تسجيل موافقتك…',
    shownIn: 'معروض بلغة',
    failed: 'تعذّر تسجيل موافقتك. يرجى المحاولة مرة أخرى.',
    pdfNote: 'سيتم حفظ نسخة موقّعة في مستنداتك.',
  },
};

export default function NdaConsentPage() {
  const { user, loading: authLoading, logout, refreshUser } = useAuth();
  const { isRTL } = useLanguage();
  const t = COPY[isRTL ? 'ar' : 'en'];

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${API_URL}/nda/status`);
        if (!cancelled) setStatus(response.data);
      } catch (err) {
        if (!cancelled) setError(t.failed);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A short text can fit without scrolling; that must still count as read.
  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atEnd = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    if (atEnd) setReachedEnd(true);
  }, []);

  useEffect(() => { measure(); }, [status, measure]);

  const handleAgree = async () => {
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API_URL}/nda/accept`, {});
      setDone(true);
      await refreshUser();
    } catch (err) {
      setError(err.response?.data?.detail?.error || t.failed);
      setSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-brand-orange" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (done && user && !user.nda_required) return <Navigate to="/dashboard" replace />;

  const docDirection = status?.direction === 'rtl' ? 'rtl' : 'ltr';

  return (
    <div
      className="min-h-screen bg-brand-cream flex items-start sm:items-center justify-center p-4 sm:p-8"
      data-testid="nda-consent-page"
    >
      <div className="w-full max-w-3xl">
        <div className="mb-4 flex items-center gap-2 text-brand-brown" data-testid="nda-banner">
          <ShieldCheck className="h-4 w-4 shrink-0" />
          <span className="text-sm font-medium">{t.banner}</span>
        </div>

        <div className="bg-white rounded-2xl shadow-brand-md border border-brand-beige overflow-hidden">
          <div className="px-6 sm:px-9 pt-7 pb-5 border-b border-brand-beige-60">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-semibold text-brand-black tracking-tight">
                  {t.heading}
                </h1>
                <p className="mt-2 text-sm text-muted-foreground max-w-xl">{t.intro}</p>
              </div>
              <div className="hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-orange/10">
                <FileText className="h-5 w-5 text-brand-orange" />
              </div>
            </div>
            {status && (
              <div
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-beige-40 px-3 py-1 text-xs text-brand-black/70"
                data-testid="nda-language-badge"
              >
                {t.shownIn}
                <span className="font-medium text-brand-black">
                  {status.language_name_native} · {status.language_name_en}
                </span>
              </div>
            )}
          </div>

          {loading ? (
            <div className="p-12 flex justify-center" data-testid="nda-loading">
              <Loader2 className="h-6 w-6 animate-spin text-brand-orange" />
            </div>
          ) : (
            <>
              <div
                ref={scrollRef}
                onScroll={measure}
                dir={docDirection}
                className="max-h-[42vh] overflow-y-auto px-6 sm:px-9 py-7 bg-brand-beige-40/40"
                data-testid="nda-text-scroll"
              >
                <h2
                  className="text-base md:text-lg font-semibold text-brand-black mb-5 leading-relaxed"
                  data-testid="nda-title"
                >
                  {status?.title}
                </h2>
                <div className="space-y-5" data-testid="nda-paragraphs">
                  {(status?.paragraphs || []).map((p, i) => (
                    <p
                      key={i}
                      className="text-sm sm:text-base leading-8 text-brand-black/85"
                      data-testid={`nda-paragraph-${i}`}
                    >
                      {p}
                    </p>
                  ))}
                </div>
              </div>

              <div className="px-6 sm:px-9 py-6 space-y-5">
                {!reachedEnd && (
                  <p className="text-xs text-muted-foreground" data-testid="nda-scroll-hint">
                    {t.scrollHint}
                  </p>
                )}

                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(v) => setChecked(Boolean(v))}
                    disabled={!reachedEnd || submitting}
                    className="mt-0.5"
                    data-testid="nda-confirm-checkbox"
                  />
                  <span className="text-sm text-brand-black/90 leading-relaxed">
                    {t.confirm}
                  </span>
                </label>

                {error && (
                  <div
                    className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700"
                    data-testid="nda-error"
                  >
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-3 sm:justify-between pt-1">
                  <Button
                    variant="ghost"
                    onClick={logout}
                    disabled={submitting}
                    className="text-muted-foreground hover:text-brand-black"
                    data-testid="nda-sign-out-btn"
                  >
                    <LogOut className="h-4 w-4 me-2" />
                    {t.signOut}
                  </Button>

                  <Button
                    onClick={handleAgree}
                    disabled={!checked || !reachedEnd || submitting}
                    className="bg-brand-orange hover:bg-brand-orange-80 text-white rounded-full px-7 shadow-brand-orange transition-colors"
                    data-testid="nda-agree-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 me-2 animate-spin" />
                        {t.saving}
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 me-2" />
                        {t.agree}
                      </>
                    )}
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground pt-1" data-testid="nda-pdf-note">
                  {t.pdfNote}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
